import React, { forwardRef } from "react";
import DiamondShapeSVG from "../DiamondShapeSVG";

const fmtVal = (v) =>
  typeof v === "boolean" ? (v ? "Yes" : "No") : v === null || v === undefined ? "—" : String(v);

const ProductCatalog = forwardRef(({
  product,
  buyer,
  cardScale = 100,
  productImageRef,
  visibleFields = {},
  cssVars = {},
  forExport = false,
}, ref) => {
  const isVisible = (key) => visibleFields[key] !== false;

  const formatPrice = (v) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR",
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(v);

  // ── resolve fields from both carddemo and real seller product shape ──
  const productName = product?.name || product?.productName || "Fine Jewellery";
  const segment     = product?.segment  || "";
  const category    = product?.category || "";

  // Images: image_url[] (normalised) OR images[{url}] (seller product)
  const imageUrls = (() => {
    if (Array.isArray(product?.image_url) && product.image_url.length)
      return product.image_url.slice(0, 3);
    if (typeof product?.image_url === "string" && product.image_url)
      return [product.image_url];
    if (Array.isArray(product?.images) && product.images.length)
      return product.images
        .map((i) => (typeof i === "string" ? i : i.url || ""))
        .filter(Boolean)
        .slice(0, 3);
    return [];
  })();

  const mainImage = imageUrls[0] || "";

  // avg gross wt per piece from lotDetails, fallback to root
  const avgGrossWt = product?.lotDetails?.[0]?.avgGrossWt || product?.grossWt || "";

  const price = product?.pricing?.retail || product?.attributes?.retail_price || 0;

  // Build attribute grid — segment, category, avg gross wt, purity, style
  // NO wastage — retail price already bakes margin in
  const hasExistingAttrs = product?.attributes && Object.keys(product.attributes).some(
    (k) => !["retail_price", "Segment", "Category"].includes(k) &&
           product.attributes[k] !== undefined && product.attributes[k] !== ""
  );

  const coreFields = [
    segment    && ["Segment",  segment],
    category   && ["Category", category],
    avgGrossWt && ["Weight",   `${parseFloat(avgGrossWt).toFixed(3)} g`],
    product?.purity && ["Purity", `${product.purity}%`],
    product?.specification && product.specification !== "PLANE" && ["Specification", product.specification],
    (product?.styleType || product?.finishType) && ["Style", product.styleType || product.finishType],
  ].filter(Boolean);

  const extraAttrs = hasExistingAttrs
    ? Object.entries(product.attributes)
        .filter(([k]) => !["retail_price", "Segment", "Category"].includes(k) && isVisible(k))
    : [];

  const segmentAttrEntries = [...coreFields, ...extraAttrs];

  const rootStyle = {
    transform: forExport ? "scale(1)" : `scale(${cardScale / 100})`,
    transformOrigin: "center top",
    width: "420px",
    minHeight: "560px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...cssVars,
  };

  return (
    <div ref={ref} style={rootStyle} className="card-preview-wrapper layout-product-catalog">

      {/* ── Background ── */}
      <div style={{ position: "absolute", inset: 0, background: "var(--theme-bg)", zIndex: 0 }} />

      {/* ── Decorative: left accent border ── */}
      <div style={{
        position: "absolute", top: 0, left: 0, bottom: 0, width: 6,
        background: "var(--theme-accent)", zIndex: 2,
      }} />
      {/* ── Decorative: right accent border ── */}
      <div style={{
        position: "absolute", top: 0, right: 0, bottom: 0, width: 6,
        background: "var(--theme-accent)", zIndex: 2,
      }} />
      {/* ── Decorative: cross-hatch behind image ── */}
      <div style={{
        position: "absolute", top: 48, left: 6, right: 6, height: 220,
        backgroundImage: `repeating-linear-gradient(45deg,var(--theme-border) 0px,var(--theme-border) 1px,transparent 1px,transparent 8px)`,
        opacity: 0.4, zIndex: 1,
      }} />
      {/* ── Decorative: faint circle ── */}
      <div style={{
        position: "absolute", top: 30, right: -50, width: 180, height: 180,
        borderRadius: "50%", border: "2px solid var(--theme-accent)", opacity: 0.12, zIndex: 1,
      }} />

      {/* ── Content ── */}
      <div style={{
        position: "relative", zIndex: 4,
        display: "flex", flexDirection: "column",
        padding: "0 16px 16px", flex: 1,
      }}>

        {/* ── Header bar ── */}
        <div style={{
          background: "var(--theme-surface)",
          borderBottom: "2px solid var(--theme-accent)",
          padding: "10px 16px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          margin: "0 -16px 12px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isVisible("logoUrl") && buyer?.logoUrl && (
              <img src={buyer.logoUrl} alt="Logo"
                style={{ width: 28, height: 28, objectFit: "contain", borderRadius: 4 }} />
            )}
            <div>
              {isVisible("organizationName") && (
                <div style={{
                  fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
                  fontSize: "0.78rem", fontWeight: 800,
                  color: "var(--theme-text)",
                  textTransform: "uppercase", letterSpacing: "0.08em",
                }}>
                  {buyer?.organizationName || "YOUR BRAND"}
                </div>
              )}
              <div style={{
                fontSize: "0.55rem", fontWeight: 600,
                color: "var(--theme-accent)",
                textTransform: "uppercase", letterSpacing: "0.12em",
                fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
              }}>
                {segment || "Jewellery"} Collection
              </div>
            </div>
          </div>
          {/* Hallmark badge */}
          {product?.attributes?.hallmarked && (
            <div style={{
              background: "var(--theme-accent)", color: "var(--theme-bg)",
              fontSize: "0.55rem", fontWeight: 800,
              padding: "4px 8px", borderRadius: 3, letterSpacing: "0.05em",
              fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
            }}>
              ✓ HALLMARKED
            </div>
          )}
        </div>

        {/* ── Image + thumb strip ── */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "stretch" }}>
          {/* Main image */}
          <div style={{
            flex: 1, background: "var(--theme-surface)",
            border: "1px solid var(--theme-border)", borderRadius: 4,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 8, minHeight: 170, position: "relative", overflow: "hidden",
          }}>
            <img
              src={mainImage}
              alt={productName}
              crossOrigin="anonymous"
              style={{ maxWidth: "100%", maxHeight: 170, objectFit: "contain" }}
            />
          </div>
          {/* Thumb strip */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, width: 60 }}>
            {imageUrls.map((url, i) => (
              <div key={i} style={{
                background: "var(--theme-surface)",
                border: "1px solid var(--theme-border)",
                borderRadius: 3, padding: 4, flex: 1,
                display: "flex", alignItems: "center", justifyContent: "center",
                overflow: "hidden",
              }}>
                <img src={url} alt="" crossOrigin="anonymous"
                  style={{ maxWidth: "100%", maxHeight: 48, objectFit: "contain" }} />
              </div>
            ))}
          </div>
        </div>

        {/* ── Product title ── */}
        <h1 style={{
          fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
          fontSize: "1.05rem", fontWeight: 700,
          color: "var(--theme-text)",
          margin: "0 0 4px", lineHeight: 1.25,
        }}>
          {productName}
        </h1>

        {/* ── SKU ── */}
        {product?.sku && (
          <div style={{
            fontSize: "0.58rem", color: "var(--theme-textMuted)",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
            marginBottom: 8, letterSpacing: "0.1em",
          }}>
            SKU: {product.sku}
          </div>
        )}

        {/* ── All segment attributes grid ── */}
        {segmentAttrEntries.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            gap: "4px 10px",
            background: "var(--theme-surface)",
            border: "1px solid var(--theme-border)",
            borderRadius: 4, padding: "8px 10px", marginBottom: 10,
          }}>
            {segmentAttrEntries.map(([k, v]) => (
              <div key={k} style={{ display: "flex", flexDirection: "column" }}>
                <span style={{
                  fontSize: "0.52rem", fontWeight: 600,
                  color: "var(--theme-accent)",
                  textTransform: "uppercase", letterSpacing: "0.08em",
                  fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                }}>
                  {k.replace(/_/g, " ")}
                </span>
                {k === "shape" && product?.segment?.toLowerCase() === "diamond" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                    <DiamondShapeSVG
                      shapeName={String(v)}
                      size={28}
                      color="var(--theme-accent)"
                    />
                    <span style={{
                      fontSize: "0.62rem", fontWeight: 600,
                      color: "var(--theme-text)",
                      fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                    }}>
                      {fmtVal(v)}
                    </span>
                  </div>
                ) : (
                  <span style={{
                    fontSize: "0.68rem", fontWeight: 600,
                    color: "var(--theme-text)",
                    fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                  }}>
                    {fmtVal(v)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Description ── */}
        {product?.description && (
          <p style={{
            fontSize: "0.66rem", lineHeight: 1.5,
            color: "var(--theme-textMuted)",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
            margin: "0 0 8px",
          }}>
            {product.description.length > 100
              ? product.description.slice(0, 100) + "…"
              : product.description}
          </p>
        )}

        <div style={{ flex: 1 }} />

        {/* ── Price + CTA ── */}
        <div style={{
          background: "var(--theme-surface)",
          border: "1px solid var(--theme-border)",
          borderTop: "2px solid var(--theme-accent)",
          borderRadius: "0 0 4px 4px",
          padding: "8px 12px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 8,
        }}>
          <div>
            <div style={{
              fontSize: "0.52rem", fontWeight: 700, textTransform: "uppercase",
              letterSpacing: "0.1em", color: "var(--theme-textMuted)",
              fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)", marginBottom: 2,
            }}>Retail Price</div>
            <div style={{
              fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
              fontSize: "1.1rem", fontWeight: 800, color: "var(--theme-accent)",
            }}>
              {formatPrice(price)}
            </div>
          </div>
          <div style={{
            background: "var(--theme-accent)", color: "var(--theme-bg)",
            fontSize: "0.63rem", fontWeight: 700,
            padding: "7px 14px", borderRadius: 3, letterSpacing: "0.08em",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          }}>
            ORDER NOW
          </div>
        </div>

        {/* ── Contact footer ── */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          fontSize: "0.56rem", color: "var(--theme-textMuted)",
          fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          paddingTop: 4, borderTop: "1px solid var(--theme-border)",
        }}>
          {isVisible("address") && (
            <span>📍 {buyer?.streetAddress || "123 Jewellers Lane"}</span>
          )}
          {isVisible("phone") && (
            <span>📞 {buyer?.transactionalMobile || buyer?.mobile || "+91 98765 43210"}</span>
          )}
        </div>
      </div>
    </div>
  );
});

ProductCatalog.displayName = "ProductCatalog";
export default ProductCatalog;
